import { Container } from 'pixi.js';
import type { Application } from './Application';
import type { InputManager } from './InputManager';

/** Parallax factor for the scenery layer (stations, suns, nebulae, planets). */
export const SCENERY_PARALLAX = 0.97;

export interface CameraTarget {
  x: number;
  y: number;
}

/**
 * Camera system for viewport control.
 * Provides follow, zoom, shake, and coordinate conversion.
 */
export class Camera {
  private readonly app: Application;
  private readonly input: InputManager;

  // World container that the camera moves (inverse of camera position)
  private worldContainer: Container | null = null;
  // Scenery container (stations, suns, etc.) — moves at SCENERY_PARALLAX speed
  private sceneryContainer: Container | null = null;

  // Camera position in world space
  private _scrollX: number = 0;
  private _scrollY: number = 0;
  private _zoom: number = 1;

  // Follow target
  private target: CameraTarget | null = null;
  private lerpX: number = 0.1;
  private lerpY: number = 0.1;

  // Shake effect
  private shakeIntensity: number = 0;
  private shakeDuration: number = 0;
  private shakeElapsed: number = 0;
  private shakeOffsetX: number = 0;
  private shakeOffsetY: number = 0;

  // Zoom limits
  private minZoom: number = 0.1;
  private maxZoom: number = 3;

  // Elastic pan offset (for right-drag panning)
  private _panOffsetX: number = 0;
  private _panOffsetY: number = 0;
  private panSnapBackSpeed: number = 0.15; // Lerp speed for snap-back
  private isPanning: boolean = false;
  private maxPanDistance: number = 400; // Max pan distance in screen pixels

  constructor(app: Application, input: InputManager) {
    this.app = app;
    this.input = input;

    // Set up world position converters for input
    this.input.setWorldPositionConverter(
      (screenX) => this.getWorldX(screenX),
      (screenY) => this.getWorldY(screenY)
    );
  }

  /**
   * Set the world container (moved inversely to camera)
   * Pass null to disable camera transforms (e.g., when switching to a scene that doesn't use camera)
   */
  setWorldContainer(container: Container | null): void {
    this.worldContainer = container;
  }

  /**
   * Set the scenery container (moves at SCENERY_PARALLAX of camera speed)
   */
  setSceneryContainer(container: Container | null): void {
    this.sceneryContainer = container;
  }

  /**
   * Clear the world container reference (for scene transitions)
   */
  clearWorldContainer(): void {
    this.worldContainer = null;
    this.sceneryContainer = null;
  }

  /**
   * Start following a target
   */
  startFollow(target: CameraTarget, _roundPixels?: boolean, lerpX?: number, lerpY?: number): void {
    this.target = target;
    this.lerpX = lerpX ?? 0.1;
    this.lerpY = lerpY ?? this.lerpX;

    // Immediately snap to target
    if (target) {
      this._scrollX = target.x;
      this._scrollY = target.y;
    }
  }

  /**
   * Update lerp speed (e.g. snap during warp)
   */
  setLerp(lerpX: number, lerpY?: number): void {
    this.lerpX = lerpX;
    this.lerpY = lerpY ?? lerpX;
  }

  /**
   * Stop following
   */
  stopFollow(): void {
    this.target = null;
  }

  /**
   * Set zoom level
   */
  setZoom(zoom: number): void {
    this._zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
  }

  /**
   * Get current zoom level
   */
  get zoom(): number {
    return this._zoom;
  }

  /**
   * Set zoom limits
   */
  setZoomLimits(min: number, max: number): void {
    this.minZoom = min;
    this.maxZoom = max;
    this._zoom = Math.max(min, Math.min(max, this._zoom));
  }

  /**
   * Camera shake effect
   */
  shake(duration: number = 200, intensity: number = 0.02): void {
    this.shakeDuration = duration;
    this.shakeIntensity = intensity;
    this.shakeElapsed = 0;
  }

  /**
   * Start panning (called when right-drag starts)
   */
  startPan(): void {
    this.isPanning = true;
  }

  /**
   * Set pan offset (called during right-drag)
   * Offset is in screen pixels, with elastic resistance at edges
   */
  setPanOffset(dx: number, dy: number): void {
    // Apply elastic resistance as we approach max distance
    const applyElastic = (value: number, max: number): number => {
      const absValue = Math.abs(value);
      if (absValue < max) {
        return value;
      }
      // Beyond max, apply diminishing returns
      const overflow = absValue - max;
      const dampedOverflow = max * 0.3 * (1 - Math.exp(-overflow / (max * 0.5)));
      return Math.sign(value) * (max + dampedOverflow);
    };

    this._panOffsetX = applyElastic(dx, this.maxPanDistance);
    this._panOffsetY = applyElastic(dy, this.maxPanDistance);
  }

  /**
   * Stop panning (called when right-drag ends)
   * Camera will snap back to center
   */
  stopPan(): void {
    this.isPanning = false;
  }

  /**
   * Get current pan offset
   */
  get panOffsetX(): number {
    return this._panOffsetX;
  }

  get panOffsetY(): number {
    return this._panOffsetY;
  }

  /**
   * Check if camera is currently being panned
   */
  get panning(): boolean {
    return this.isPanning;
  }

  /**
   * Get camera scroll position (center of viewport in world coordinates)
   */
  get scrollX(): number {
    return this._scrollX;
  }

  get scrollY(): number {
    return this._scrollY;
  }

  /**
   * Set camera position directly
   */
  setScroll(x: number, y: number): void {
    this._scrollX = x;
    this._scrollY = y;
  }

  /**
   * Get viewport dimensions
   */
  get width(): number {
    return this.app.initialized ? this.app.screen.width : window.innerWidth;
  }

  get height(): number {
    return this.app.initialized ? this.app.screen.height : window.innerHeight;
  }

  /**
   * Get viewport bounds in world coordinates
   */
  get worldView(): { x: number; y: number; width: number; height: number } {
    const halfWidth = this.width / 2 / this._zoom;
    const halfHeight = this.height / 2 / this._zoom;

    return {
      x: this._scrollX - halfWidth,
      y: this._scrollY - halfHeight,
      width: halfWidth * 2,
      height: halfHeight * 2,
    };
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  getWorldX(screenX: number): number {
    const centerX = this.width / 2;
    return this._scrollX + (screenX - centerX) / this._zoom;
  }

  getWorldY(screenY: number): number {
    const centerY = this.height / 2;
    return this._scrollY + (screenY - centerY) / this._zoom;
  }

  /**
   * Convert world coordinates to screen coordinates
   */
  getScreenX(worldX: number): number {
    const centerX = this.width / 2;
    return centerX + (worldX - this._scrollX) * this._zoom;
  }

  getScreenY(worldY: number): number {
    const centerY = this.height / 2;
    return centerY + (worldY - this._scrollY) * this._zoom;
  }

  /**
   * Check if a world point is visible on screen
   */
  isVisible(worldX: number, worldY: number, margin: number = 0): boolean {
    const view = this.worldView;
    return (
      worldX >= view.x - margin &&
      worldX <= view.x + view.width + margin &&
      worldY >= view.y - margin &&
      worldY <= view.y + view.height + margin
    );
  }

  /**
   * Update camera - called every frame
   */
  update(delta: number): void {
    // Follow target with lerp
    if (this.target) {
      const dx = this.target.x - this._scrollX;
      const dy = this.target.y - this._scrollY;

      this._scrollX += dx * this.lerpX;
      this._scrollY += dy * this.lerpY;
    }

    // Update shake
    if (this.shakeElapsed < this.shakeDuration) {
      this.shakeElapsed += delta;
      const progress = this.shakeElapsed / this.shakeDuration;
      const intensity = this.shakeIntensity * (1 - progress);

      this.shakeOffsetX = (Math.random() * 2 - 1) * intensity * this.width;
      this.shakeOffsetY = (Math.random() * 2 - 1) * intensity * this.height;
    } else {
      this.shakeOffsetX = 0;
      this.shakeOffsetY = 0;
    }

    // Update elastic pan offset (snap back when not panning)
    if (!this.isPanning) {
      // Lerp back to zero
      this._panOffsetX *= (1 - this.panSnapBackSpeed);
      this._panOffsetY *= (1 - this.panSnapBackSpeed);

      // Snap to zero when close enough
      if (Math.abs(this._panOffsetX) < 0.5) this._panOffsetX = 0;
      if (Math.abs(this._panOffsetY) < 0.5) this._panOffsetY = 0;
    }

    // Apply camera transform to world container
    if (this.worldContainer) {
      const centerX = this.width / 2;
      const centerY = this.height / 2;

      // Position world container so camera target is at screen center
      // Include pan offset for elastic panning effect
      this.worldContainer.x = centerX - this._scrollX * this._zoom + this.shakeOffsetX + this._panOffsetX;
      this.worldContainer.y = centerY - this._scrollY * this._zoom + this.shakeOffsetY + this._panOffsetY;
      this.worldContainer.scale.set(this._zoom);
    }

    // Position scenery container with subtle parallax
    if (this.sceneryContainer) {
      const centerX = this.width / 2;
      const centerY = this.height / 2;
      this.sceneryContainer.x = centerX - this._scrollX * this._zoom * SCENERY_PARALLAX + this.shakeOffsetX + this._panOffsetX;
      this.sceneryContainer.y = centerY - this._scrollY * this._zoom * SCENERY_PARALLAX + this.shakeOffsetY + this._panOffsetY;
      this.sceneryContainer.scale.set(this._zoom);
    }
  }
}
